import React from 'react'
import {FormattedDate} from 'react-intl'
import {useSelector} from 'react-redux'
import {ContentHotel} from '@findhotel/sapi'
import {getSearchParameters} from 'modules/sapiSearch/selectors'
import {dateStringToMiddayDate} from 'utils'

import {Text} from '@vio/atlas/Text'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {formatCheckInOutTime} from '@vio/core/src/utils/date'

import {isCheckInCheckOutAvailable} from '../utils'
import {CheckInCheckOutLoading} from './CheckInCheckOutLoading'
import {
  CheckInOutWrapper,
  CheckInTextContainer,
  Circle,
  DetailsWrapper,
  DottedLine,
  DottedLineContainer,
  SecondaryRow,
  Wrapper
} from './styles'

export type CheckInCheckOutLayout = 'primary' | 'secondary'

export interface CheckInCheckOutProps {
  hotel?: ContentHotel
  isLoading: boolean
  layout: CheckInCheckOutLayout
}

export const CheckInCheckOut: React.FC<CheckInCheckOutProps> = ({
  hotel,
  isLoading,
  layout
}) => {
  const {checkIn, checkOut} = useSelector(getSearchParameters)

  // in case of SSR without any params we want to show Skeleton loader
  if (isLoading || !hotel) return <CheckInCheckOutLoading layout={layout} />

  // Hide this component if the required details are not available.
  if (!isCheckInCheckOutAvailable(hotel)) return null

  return layout === 'primary' ? (
    <Wrapper className="--layout-primary">
      <CheckInOutWrapper>
        <DetailsWrapper>
          <Text variant={'bodyXS'}>
            <FormattedMessageWrapper id="checkIn" defaultMessage="Check-in" />
          </Text>
          <CheckInTextContainer variant={'labelM'}>
            {hotel.checkInEnd ? (
              <FormattedMessageWrapper
                id="checkInStartEnd"
                defaultMessage="From {checkInStart} to {checkInEnd}"
                values={{
                  checkInStart: formatCheckInOutTime(hotel.checkInTime),
                  checkInEnd: formatCheckInOutTime(hotel.checkInEnd)
                }}
              />
            ) : (
              <FormattedMessageWrapper
                id="fromCheckInTime"
                defaultMessage="Check-in from {checkInTime}"
                values={{
                  checkInTime: formatCheckInOutTime(hotel.checkInTime)
                }}
              />
            )}
          </CheckInTextContainer>
        </DetailsWrapper>
      </CheckInOutWrapper>
      <DottedLineContainer>
        <Circle />
        <DottedLine />
        <Circle />
      </DottedLineContainer>
      <CheckInOutWrapper>
        <DetailsWrapper alignedRight>
          <Text variant={'bodyXS'}>
            <FormattedMessageWrapper id="checkOut" defaultMessage="Check-out" />
          </Text>
          <CheckInTextContainer variant={'labelM'}>
            <FormattedMessageWrapper
              id="beforeCheckoutTime"
              defaultMessage="Before {checkOutTime}"
              values={{checkOutTime: formatCheckInOutTime(hotel.checkOutTime)}}
            />
          </CheckInTextContainer>
        </DetailsWrapper>
      </CheckInOutWrapper>
    </Wrapper>
  ) : (
    <Wrapper>
      <SecondaryRow>
        <Text variant="bodyS">
          <FormattedMessageWrapper id="checkIn" defaultMessage="Check-in" />
        </Text>
        <Text variant="bodyS">
          <FormattedMessageWrapper id="checkOut" defaultMessage="Check-out" />
        </Text>
      </SecondaryRow>

      <SecondaryRow className="--has-gap">
        <Text variant="labelM">
          {checkIn && (
            <FormattedDate
              value={dateStringToMiddayDate(checkIn)}
              month="short"
              day="2-digit"
              weekday="short"
            />
          )}
        </Text>
        <DottedLineContainer>
          <Circle />
          <DottedLine />
          <Circle />
        </DottedLineContainer>
        <CheckInOutWrapper></CheckInOutWrapper>
        <Text variant="labelM">
          {checkOut && (
            <FormattedDate
              value={dateStringToMiddayDate(checkOut)}
              month="short"
              day="2-digit"
              weekday="short"
            />
          )}
        </Text>
      </SecondaryRow>

      <SecondaryRow>
        <CheckInTextContainer variant="bodyS">
          {hotel.checkInEnd ? (
            <FormattedMessageWrapper
              id="checkInStartEnd"
              defaultMessage="From {checkInStart} to {checkInEnd}"
              values={{
                checkInStart: formatCheckInOutTime(hotel.checkInTime),
                checkInEnd: formatCheckInOutTime(hotel.checkInEnd)
              }}
            />
          ) : (
            <FormattedMessageWrapper
              id="fromCheckInTime"
              defaultMessage="Check-in from {checkInTime}"
              values={{
                checkInTime: formatCheckInOutTime(hotel.checkInTime)
              }}
            />
          )}
        </CheckInTextContainer>
        <CheckInTextContainer variant="bodyS">
          <FormattedMessageWrapper
            id="beforeCheckoutTime"
            defaultMessage="Before {checkOutTime}"
            values={{
              checkOutTime: formatCheckInOutTime(hotel.checkOutTime)
            }}
          />
        </CheckInTextContainer>
      </SecondaryRow>
    </Wrapper>
  )
}
